// ─── What: bounded declutter for the trade overlay ────────────────────────────
//
// A zoomed-out window can hold thousands of trades, and the renderer appends one
// SVG path per mark. Drawing them all is both unreadable and slow, but dropping
// the small ones erases the shape of retail activity — a wall of tiny buys is
// information, not noise.
//
// So: the trades worth reading individually stay individual (the biggest, and
// anything belonging to a highlighted trader), and the rest merge into clusters
// on a pixel grid. Nothing is discarded. Zooming in spreads trades across more
// cells, so clusters dissolve back into real trades on their own.
//
// The cell grows until the mark count fits the budget, so the clustered part is
// bounded at any density rather than only at plausible ones.
//
// Pinned trades are the deliberate exception: they are never merged, so the
// budget does not bound them. Highlighting an account with thousands of window
// trades draws thousands of marks. That is the point of highlighting one — and
// it is what the chart did for highlighted traders before clustering existed —
// but it means `budget` bounds the clustered remainder, not the total.

export interface ClusterInput {
    key: string;
    /** Screen position, already through the chart's scales. */
    x: number;
    y: number;
    quoteAmount: number;
    side: 'buy' | 'sell';
    /** Highlighted-trader trades are never merged away. */
    pinned: boolean;
    /** Optional timestamp, so a cluster can report the span it covers. */
    ms?: number;
    /** Optional traded price, so a cluster can report its average. */
    price?: number;
}

export interface TradeCluster {
    x: number;
    y: number;
    totalQuote: number;
    side: 'buy' | 'sell';
    count: number;
    /** Member keys, for list-to-chart hover resolution. */
    keys: string[];
    /** Set only when count === 1, so the caller can render a real trade. */
    key: string | null;
    minMs: number | null;
    maxMs: number | null;
    /** Volume-weighted mean price, or null when no member carried one. */
    avgPrice: number | null;
}

export interface ClusterOptions {
    /** Biggest N trades always drawn individually. */
    whaleCount?: number;
    /** Ceiling on marks returned. Bounds the clustered remainder, not pinned. */
    budget?: number;
    /** Starting grid cell, in pixels. */
    cellPx?: number;
    /** How many times the cell may grow before giving up. */
    maxPasses?: number;
}

const DEFAULTS = {
    whaleCount: 50,
    budget: 300,
    cellPx: 6,
    maxPasses: 8,
};

/** Each cell grows by this factor per pass — enough to converge quickly. */
const GROWTH = 1.6;

function singleton(t: ClusterInput): TradeCluster {
    return {
        x: t.x,
        y: t.y,
        totalQuote: t.quoteAmount,
        side: t.side,
        count: 1,
        keys: [t.key],
        key: t.key,
        minMs: t.ms ?? null,
        maxMs: t.ms ?? null,
        avgPrice: t.price ?? null,
    };
}

function binKey(t: ClusterInput, cell: number): string {
    return `${Math.floor(t.x / cell)}|${Math.floor(t.y / cell)}|${t.side}`;
}

function collapse(members: ClusterInput[]): TradeCluster {
    if (members.length === 1) return singleton(members[0]);

    const totalQuote = members.reduce((sum, m) => sum + m.quoteAmount, 0);
    // Weight the centre by size, so a cluster sits where its money is. With no
    // volume to weight by, the plain mean is the only honest answer.
    let x = 0;
    let y = 0;
    if (totalQuote > 0) {
        for (const m of members) {
            x += m.x * (m.quoteAmount / totalQuote);
            y += m.y * (m.quoteAmount / totalQuote);
        }
    } else {
        for (const m of members) {
            x += m.x / members.length;
            y += m.y / members.length;
        }
    }

    let minMs: number | null = null;
    let maxMs: number | null = null;
    for (const m of members) {
        if (m.ms === undefined) continue;
        if (minMs === null || m.ms < minMs) minMs = m.ms;
        if (maxMs === null || m.ms > maxMs) maxMs = m.ms;
    }

    // Weighted by size for the same reason the centroid is: the average a
    // reader wants is the average dollar's price, not the average ticket's.
    const priced = members.filter(m => m.price !== undefined);
    let avgPrice: number | null = null;
    if (priced.length > 0) {
        const pricedVol = priced.reduce((sum, m) => sum + m.quoteAmount, 0);
        avgPrice =
            pricedVol > 0
                ? priced.reduce(
                      (sum, m) => sum + m.price! * (m.quoteAmount / pricedVol),
                      0
                  )
                : priced.reduce((sum, m) => sum + m.price!, 0) / priced.length;
    }

    return {
        x,
        y,
        totalQuote,
        side: members[0].side,
        count: members.length,
        keys: members.map(m => m.key),
        key: null,
        minMs,
        maxMs,
        avgPrice,
    };
}

export function clusterTrades(
    input: ClusterInput[],
    options: ClusterOptions = {}
): TradeCluster[] {
    const { whaleCount, budget, cellPx, maxPasses } = {
        ...DEFAULTS,
        ...options,
    };
    if (input.length === 0) return [];

    // Rank by size, breaking ties on key so the whale set — and therefore the
    // whole output — is stable across renders of the same data.
    const ranked = [...input].sort(
        (a, b) =>
            b.quoteAmount - a.quoteAmount ||
            (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
    );

    const pinned: ClusterInput[] = [];
    const rest: ClusterInput[] = [];
    let whalesTaken = 0;
    for (const t of ranked) {
        if (t.pinned) {
            pinned.push(t);
        } else if (whalesTaken < whaleCount) {
            pinned.push(t);
            whalesTaken++;
        } else {
            rest.push(t);
        }
    }

    let cell = Math.max(1, cellPx);
    let bins = new Map<string, ClusterInput[]>();
    for (let pass = 0; pass < maxPasses; pass++) {
        bins = new Map();
        for (const t of rest) {
            const k = binKey(t, cell);
            const bucket = bins.get(k);
            if (bucket) bucket.push(t);
            else bins.set(k, [t]);
        }
        if (pinned.length + bins.size <= budget) break;
        cell *= GROWTH;
    }

    const out = [...pinned.map(singleton), ...[...bins.values()].map(collapse)];

    // Largest first, so the caller draws big marks first and small ones land on
    // top where they stay hoverable. Ties break on key for determinism.
    out.sort(
        (a, b) =>
            b.totalQuote - a.totalQuote ||
            (a.keys[0] < b.keys[0] ? -1 : a.keys[0] > b.keys[0] ? 1 : 0)
    );
    return out;
}
